import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const AnnouncementService = {
  getAnnouncements: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/announcements`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching announcements:", error);
      throw error;
    }
  },
  getAnnouncementsByDepartment: async (departmentId, companyId, token, userType) => {
    try {
      const response = await axios.get(
        `${API_URL}/announcements/department/${departmentId}`, {
          headers: {
            'user-id': token,
            'user-type': userType,
            'company-id': companyId
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching department announcements:", error);
      throw error;
    }
  },
  addAnnouncement: async (announcement, companyId, token, userType) => {
    try {
      const response = await axios.post(`${API_URL}/announcements`, announcement, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error adding announcement:", error);
      throw error;
    }
  },
  deleteAnnouncement: async (id, companyId, token, userType) => {
    try {
      await axios.delete(`${API_URL}/announcements/${id}`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
    } catch (error) {
      console.error("Error deleting announcement:", error);
      throw error;
    }
  },
  updateAnnouncement: async (id, announcement, companyId, token, userType) => {
    try {
      const response = await axios.put(
        `${API_URL}/announcements/${id}`,
        announcement,
        {
          headers: {
            'user-id': token,
            'user-type': userType,
            'company-id': companyId
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating announcement:", error);
      throw error;
    }
  },
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const AnnouncementForm = ({ onAnnouncementAdded, departments, companyId, token, userType }) => {
  const [form, setForm] = useState({
    title: "",
    content: "",
    department_id: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      const formData = { 
        ...form, 
        department_id: form.department_id === "" ? null : form.department_id 
      };
      
      await AnnouncementService.addAnnouncement(formData, companyId, token, userType);
      setForm({ title: "", content: "", department_id: "" });
      setSuccess("Announcement added successfully!");
      onAnnouncementAdded();
    } catch (err) {
      console.error("Error adding announcement:", err);
      setError(err.response?.data?.error || "Failed to add announcement");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto"
    >
      <h3 className="text-xl font-bold text-gray-700 mb-4">Add Announcement</h3>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 p-4 rounded-lg mb-4">
          {success}
        </div>
      )}
      
      <input
        name="title"
        placeholder="Title"
        value={form.title}
        onChange={handleChange}
        required
        className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <textarea
        name="content"
        placeholder="Content"
        value={form.content}
        onChange={handleChange}
        required
        rows="4"
        className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <select
        name="department_id"
        value={form.department_id}
        onChange={handleChange}
        className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="">-- Select Department (Optional) --</option>
        {departments.map((department) => (
          <option key={department.id} value={department.id}>
            {department.dname}
          </option>
        ))}
      </select>
      <button
        type="submit"
        className="w-full bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700"
      >
        Post Announcement
      </button>
    </form>
  );
};

const AnnouncementCard = ({ announcement, onDeleteAnnouncement, companyId, token, userType }) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
    <div className="p-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-bold text-gray-800">{announcement.title}</h3>
        <button
          onClick={() => onDeleteAnnouncement(announcement.id, companyId, token, userType)}
          className="text-red-600 hover:text-red-800"
        >
          Delete
        </button>
      </div>
      <p className="text-gray-600 mb-3 whitespace-pre-wrap">{announcement.content}</p>
      <div className="flex justify-between items-center text-sm text-gray-500">
        <span>
          {announcement.department_name
            ? `Department: ${announcement.department_name}`
            : "General Announcement"}
        </span>
        <span>Posted: {formatDate(announcement.created_at)}</span>
      </div>
    </div>
  </div>
);

const AnnouncementList = ({ announcements, onDeleteAnnouncement, companyId, token, userType }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg max-w-4xl mx-auto">
    <h3 className="text-xl font-bold text-gray-700 mb-4">Announcements</h3>
    <div className="w-full overflow-auto max-h-[70vh]">
      {announcements.length === 0 ? (
        <p className="text-gray-500 text-center py-4">No announcements available.</p>
      ) : (
        announcements.map((announcement) => (
          <AnnouncementCard
            key={announcement.id}
            announcement={announcement}
            onDeleteAnnouncement={onDeleteAnnouncement}
            companyId={companyId}
            token={token}
            userType={userType}
          />
        ))
      )}
    </div>
  </div>
);

const DepartmentFilter = ({ departments, selectedDepartment, onDepartmentChange }) => (
  <div className="bg-white p-4 rounded-xl shadow-lg max-w-4xl mx-auto mb-6">
    <div className="flex items-center">
      <span className="text-gray-700 font-medium mr-3">Filter by Department:</span>
      <select
        value={selectedDepartment || ""}
        onChange={(e) => onDepartmentChange(e.target.value === "" ? null : e.target.value)}
        className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="">All Announcements</option>
        {departments.map((department) => (
          <option key={department.id} value={department.id}>
            {department.dname}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const AnnouncementsPage = () => {
  const { user, userType, companyId, logout } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const fetchDepartments = async () => {
    if (!companyId) {
      logout();
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/departments`, {
        headers: {
          'user-id': token,
          'user-type': userType,
          'company-id': companyId
        }
      });
      setDepartments(response.data);
    } catch (err) {
      console.error("Error fetching departments:", err);
      setError("Failed to fetch departments. Please try again later.");
    }
  };

  const fetchAnnouncements = async () => {
    if (!companyId) {
      logout();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let data;
      if (selectedDepartment) {
        data = await AnnouncementService.getAnnouncementsByDepartment(
          selectedDepartment, 
          companyId, 
          token, 
          userType
        );
      } else {
        data = await AnnouncementService.getAnnouncements(
          companyId, 
          token, 
          userType
        );
      }
      setAnnouncements(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching announcements:", err);
      setError("Failed to fetch announcements. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await AnnouncementService.deleteAnnouncement(id, companyId, token, userType);
      setAnnouncements((prevAnnouncements) =>
        prevAnnouncements.filter((announcement) => announcement.id !== id)
      );
      setError(null);
    } catch (err) {
      console.error("Error deleting announcement:", err);
      setError("Failed to delete announcement. Please try again later.");
    }
  };

  const handleDepartmentChange = (departmentId) => {
    setSelectedDepartment(departmentId);
  };

  useEffect(() => {
    fetchDepartments();
  }, [companyId]);

  useEffect(() => {
    fetchAnnouncements();
  }, [selectedDepartment, companyId]);

  return (
    <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
      <h1 className="text-4xl font-bold text-white mb-6 text-center">
        Announcement Management
      </h1>
      
      {companyId ? (
        <>
          <AnnouncementForm 
            onAnnouncementAdded={fetchAnnouncements} 
            departments={departments}
            companyId={companyId}
            token={localStorage.getItem('token')}
            userType={userType}
          />
          
          <DepartmentFilter
            departments={departments}
            selectedDepartment={selectedDepartment}
            onDepartmentChange={handleDepartmentChange}
          />
          
          {loading ? (
            <div className="text-center text-lg text-white">Loading...</div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg max-w-4xl mx-auto">
              {error}
            </div>
          ) : (
            <AnnouncementList
              announcements={announcements}
              onDeleteAnnouncement={handleDeleteAnnouncement}
              companyId={companyId}
              token={localStorage.getItem('token')}
              userType={userType}
            />
          )}
        </>
      ) : (
        <div className="text-center text-lg text-white">
          Company information not available. Please login again.
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;